import Button from "@shared/components/ui/Button";
import { PlusIcon } from "@shared/components/ui/Icon";
import theme from "@shared/utils/themeColors";
import { formatDistanceToNow } from "date-fns";
import React, { useState } from "react";
import { useTutorContext } from "../context/TutorContext";
import TutorSubscriptionLimit from "./TutorSubscriptionLimit";

interface TutorConversationListProps {
  onSelectConversation: (conversationId: string) => void;
}

/**
 * Component to display the list of tutor conversations
 */
const TutorConversationList: React.FC<TutorConversationListProps> = ({
  onSelectConversation,
}) => {
  const {
    conversations,
    loading,
    createConversation,
    deleteConversation,
    error,
  } = useTutorContext();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Handle creating a new conversation
  const handleNewConversation = async () => {
    try {
      const newConversation = await createConversation();
      if (newConversation && newConversation.id) {
        onSelectConversation(newConversation.id);
      }
    } catch (error) {
      console.error("Error creating new conversation:", error);
    }
  };

  // Handle deleting a conversation
  const handleDeleteConversation = async (conversationId: string) => {
    try {
      await deleteConversation(conversationId);
      setConfirmDelete(null);
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
  };

  if (error === "subscription_limit") {
    return <TutorSubscriptionLimit />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* New conversation button */}
      <div className="p-4">
        <Button
          onClick={handleNewConversation}
          leftIcon={<PlusIcon />}
          className="w-full"
        >
          New conversation
        </Button>
      </div>

      {/* Conversation list */}
      <div className="flex-grow overflow-y-auto px-4 pb-4">
        <h4 className={`text-xs font-semibold mb-2 ${theme.text.muted}`}>
          Recent conversations
        </h4>

        {loading ? (
          <div className="flex items-center justify-center h-20">
            <div
              className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.border.brand}`}
            ></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-4 text-center">
            <p className={theme.text.muted}>No conversations yet</p>
            <p className={theme.text.muted}>
              Start a new conversation to begin
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {conversations.map((conversation) => (
              <li
                key={conversation.id}
                className="rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <div className="flex items-center w-full">
                  <button
                    onClick={() => onSelectConversation(conversation.id)}
                    className="flex-grow flex items-center p-2 text-left"
                  >
                    <div className="flex flex-col truncate">
                      <span
                        className={`text-sm font-medium truncate ${theme.text.primary}`}
                      >
                        {conversation.title}
                      </span>
                      <span className={`text-xs ${theme.text.muted}`}>
                        {formatDistanceToNow(new Date(conversation.updatedAt), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  </button>

                  {confirmDelete === conversation.id ? (
                    <div className="flex items-center space-x-1 px-1">
                      <button
                        onClick={() =>
                          handleDeleteConversation(conversation.id)
                        }
                        className="text-xs p-1 text-red-500 hover:text-red-700"
                        title="Confirm delete"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="text-xs p-1 text-gray-500 hover:text-gray-700"
                        title="Cancel"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(conversation.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-500"
                      title="Delete conversation"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TutorConversationList;
